import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/format';
import { Plus, Trash2, Save, AlertTriangle, FileImage, Eye, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import FileUpload from '../components/FileUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Minimum tarih: 01.01.2026
const MIN_DATE = '2026-01-01';

export default function ElemanEntry() {
  const { currentUser } = useApp();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [kayitVar, setKayitVar] = useState(false);
  const [mevcutKayit, setMevcutKayit] = useState(null);
  const [bankalar, setBankalar] = useState({ kartPos: [], havale: [] });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Kart ekstresi dialog
  const [showEkstreDialog, setShowEkstreDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    nakitSatis: '',
    veresiyeSatis: '',
    nakitGiderToplam: '',
    malAlimTutari: '',
    not_: '',
    kartDetaylari: [],
    havaleDetaylari: []
  });

  useEffect(() => {
    loadBankalar();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      checkDateRecord();
    }
  }, [selectedDate]);

  const loadBankalar = async () => {
    try {
      const bankaRes = await api.getBankalar();
      const allBankalar = bankaRes.data;
      setBankalar({
        kartPos: allBankalar.filter(b => b.tur === 'kartPos'),
        havale: allBankalar.filter(b => b.tur === 'havale')
      });
    } catch (error) {
      console.error('Error loading banks:', error);
      toast.error('Bankalar yüklenirken hata oluştu');
    }
  };

  const checkDateRecord = async () => {
    try {
      setLoading(true);
      const res = await api.checkTarihKayit(currentUser.subeId, selectedDate);
      
      if (res.data.exists) {
        setKayitVar(true);
        setMevcutKayit(res.data.data);
      } else {
        setKayitVar(false);
        setMevcutKayit(null);
        resetForm();
      }
    } catch (error) {
      console.error('Error checking date record:', error);
      setKayitVar(false);
      setMevcutKayit(null);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    checkDateRecord();
  };

  const handleAddKartDetay = () => {
    setFormData({
      ...formData,
      kartDetaylari: [...formData.kartDetaylari, { bankaHesabiId: '', tutar: '' }]
    });
  };

  const handleAddHavaleDetay = () => {
    setFormData({
      ...formData,
      havaleDetaylari: [...formData.havaleDetaylari, { bankaHesabiId: '', tutar: '' }]
    });
  };

  const handleRemoveKartDetay = (index) => {
    const newKartDetaylari = formData.kartDetaylari.filter((_, i) => i !== index);
    setFormData({ ...formData, kartDetaylari: newKartDetaylari });
  };

  const handleRemoveHavaleDetay = (index) => {
    const newHavaleDetaylari = formData.havaleDetaylari.filter((_, i) => i !== index);
    setFormData({ ...formData, havaleDetaylari: newHavaleDetaylari });
  };

  const handleKartDetayChange = (index, field, value) => {
    const newKartDetaylari = [...formData.kartDetaylari];
    newKartDetaylari[index][field] = value;
    setFormData({ ...formData, kartDetaylari: newKartDetaylari });
  };

  const handleHavaleDetayChange = (index, field, value) => {
    const newHavaleDetaylari = [...formData.havaleDetaylari];
    newHavaleDetaylari[index][field] = value;
    setFormData({ ...formData, havaleDetaylari: newHavaleDetaylari });
  };

  const calculateTotals = () => {
    const nakit = parseFloat(formData.nakitSatis) || 0;
    const veresiye = parseFloat(formData.veresiyeSatis) || 0;
    const gider = parseFloat(formData.nakitGiderToplam) || 0;
    const malAlim = parseFloat(formData.malAlimTutari) || 0;
    const kartToplam = formData.kartDetaylari.reduce((sum, k) => sum + (parseFloat(k.tutar) || 0), 0);
    const havaleToplam = formData.havaleDetaylari.reduce((sum, h) => sum + (parseFloat(h.tutar) || 0), 0);
    const toplamSatis = nakit + veresiye + kartToplam + havaleToplam;
    const toplamGider = gider + malAlim;
    const net = toplamSatis - toplamGider;
    const kasadakiNakit = nakit - gider - malAlim;

    return { kartToplam, havaleToplam, toplamSatis, toplamGider, net, kasadakiNakit, malAlim };
  };

  const validateAndShowConfirm = () => {
    // Validate
    const usedKartBanks = new Set();
    for (const kart of formData.kartDetaylari) {
      if (!kart.bankaHesabiId) {
        toast.error('Lütfen tüm kart POS banka seçimlerini yapın');
        return;
      }
      if (usedKartBanks.has(kart.bankaHesabiId)) {
        toast.error('Aynı bankayı birden fazla kez seçemezsiniz');
        return;
      }
      usedKartBanks.add(kart.bankaHesabiId);
    }

    const usedHavaleBanks = new Set();
    for (const havale of formData.havaleDetaylari) {
      if (!havale.bankaHesabiId) {
        toast.error('Lütfen tüm havale banka seçimlerini yapın');
        return;
      }
      if (usedHavaleBanks.has(havale.bankaHesabiId)) {
        toast.error('Aynı bankayı birden fazla kez seçemezsiniz');
        return;
      }
      usedHavaleBanks.add(havale.bankaHesabiId);
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    
    try {
      const totals = calculateTotals();
      
      const submitData = {
        tarih: selectedDate,
        subeId: currentUser.subeId,
        girenKullaniciId: currentUser.id,
        nakitSatis: parseFloat(formData.nakitSatis) || 0,
        veresiyeSatis: parseFloat(formData.veresiyeSatis) || 0,
        nakitGiderToplam: parseFloat(formData.nakitGiderToplam) || 0,
        malAlimTutari: parseFloat(formData.malAlimTutari) || 0,
        not_: formData.not_,
        kartDetaylari: formData.kartDetaylari.map(k => ({
          bankaHesabiId: k.bankaHesabiId,
          tutar: parseFloat(k.tutar) || 0
        })),
        havaleDetaylari: formData.havaleDetaylari.map(h => ({
          bankaHesabiId: h.bankaHesabiId,
          tutar: parseFloat(h.tutar) || 0
        })),
        onaylandi: true
      };

      await api.createGunSonu(submitData);
      toast.success(`${formatDateDisplay(selectedDate)} tarihli kayıt başarıyla oluşturuldu`);
      loadData();
    } catch (error) {
      console.error('Error creating record:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Kayıt oluşturulurken hata oluştu');
      }
    }
  };

  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getMaxDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textSecondary">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (kayitVar) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="existing-record-view">
        {/* Tarih Seçici */}
        <div className="bg-gradient-to-r from-primary to-orange-500 text-white rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              <span className="font-medium">Tarih Seçin</span>
            </div>
            <Input
              type="date"
              value={selectedDate}
              min={MIN_DATE}
              max={getMaxDate()}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-white/20 border-white/30 text-white placeholder:text-white/70 w-auto"
              data-testid="date-picker"
            />
          </div>
          <p className="text-xs text-white/80 mt-2">01.01.2026'dan bugüne kadar veri girebilirsiniz</p>
        </div>

        <div className="bg-surface border border-border p-6">
          <h2 className="text-2xl font-heading font-bold text-textPrimary mb-4">
            {formatDateDisplay(selectedDate)} Kaydı
          </h2>
          <p className="text-textSecondary mb-6">
            Bu tarih için gün sonu kaydı zaten girilmiş. Kayıtları görüntüleyebilirsiniz.
          </p>

          <div className="space-y-4 font-mono">
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-textSecondary">Nakit Satış:</span>
              <span className="text-textPrimary font-bold">{formatCurrency(mevcutKayit.nakitSatis)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-textSecondary">Veresiye Satış:</span>
              <span className="text-textPrimary font-bold">{formatCurrency(mevcutKayit.veresiyeSatis)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-textSecondary">Kart POS Toplam:</span>
              <span className="text-textPrimary font-bold">{formatCurrency(mevcutKayit.kartToplam)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-textSecondary">Havale Toplam:</span>
              <span className="text-textPrimary font-bold">{formatCurrency(mevcutKayit.havaleToplam)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-textSecondary">Nakit Gider:</span>
              <span className="text-error font-bold">{formatCurrency(mevcutKayit.nakitGiderToplam)}</span>
            </div>
            {mevcutKayit.malAlimTutari > 0 && (
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-textSecondary">Mal Alım Tutarı:</span>
                <span className="text-error font-bold">{formatCurrency(mevcutKayit.malAlimTutari)}</span>
              </div>
            )}
            <div className="flex justify-between py-3 bg-surfaceSubtle px-4 mt-4">
              <span className="text-textPrimary font-bold">NET:</span>
              <span className="text-2xl text-primary font-bold">{formatCurrency(mevcutKayit.net)}</span>
            </div>
          </div>

          {mevcutKayit.not_ && (
            <div className="mt-6">
              <p className="text-sm text-textSecondary mb-2">Not:</p>
              <p className="text-textPrimary">{mevcutKayit.not_}</p>
            </div>
          )}

          {/* Kart Ekstresi Butonu */}
          <div className="mt-6 pt-4 border-t border-border">
            <button
              onClick={() => setShowEkstreDialog(true)}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded transition-colors ${
                mevcutKayit.kartEkstresiUrl 
                  ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' 
                  : 'bg-surfaceSubtle text-textSecondary hover:bg-border'
              }`}
            >
              {mevcutKayit.kartEkstresiUrl ? (
                <>
                  <Eye className="h-5 w-5" />
                  Kart Ekstresini Görüntüle
                </>
              ) : (
                <>
                  <FileImage className="h-5 w-5" />
                  Kart Ekstresi Ekle
                </>
              )}
            </button>
          </div>
        </div>

        {/* Kart Ekstresi Dialog */}
        <Dialog open={showEkstreDialog} onOpenChange={setShowEkstreDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Kart Ekstresi</DialogTitle>
              <DialogDescription>
                Bugünün gün sonu kaydı için kart ekstresi görseli yükleyin
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="mb-4 p-3 bg-surfaceSubtle rounded-lg">
                <p className="text-sm"><strong>Tarih:</strong> {mevcutKayit.tarih}</p>
                <p className="text-sm"><strong>Kart Toplam:</strong> {formatCurrency(mevcutKayit.kartToplam)}</p>
              </div>
              <FileUpload
                endpoint={`/api/upload/gunsonu/${mevcutKayit.id}`}
                currentUrl={mevcutKayit.kartEkstresiUrl}
                onUploadSuccess={(url) => {
                  setMevcutKayit(prev => ({ ...prev, kartEkstresiUrl: url }));
                }}
                onDeleteSuccess={() => {
                  setMevcutKayit(prev => ({ ...prev, kartEkstresiUrl: null }));
                }}
                label="Kart Ekstresi Yükle"
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto" data-testid="entry-form">
      <h1 className="text-3xl font-heading font-bold text-textPrimary mb-6 text-center">
        Gün Sonu Kasa Girişi
      </h1>

      {/* Tarih Seçici */}
      <div className="bg-gradient-to-r from-primary to-orange-500 text-white rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            <span className="font-medium">Tarih Seçin</span>
          </div>
          <Input
            type="date"
            value={selectedDate}
            min={MIN_DATE}
            max={getMaxDate()}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-white/20 border-white/30 text-white placeholder:text-white/70 w-auto [color-scheme:dark]"
            data-testid="date-picker-form"
          />
        </div>
        <p className="text-xs text-white/80 mt-2">01.01.2026'dan bugüne kadar veri girebilirsiniz</p>
      </div>

      <div className="bg-surface border border-border p-6 space-y-6">
        {/* Nakit Satış */}
        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Nakit Satış (₺)
          </label>
          <Input
            type="number"
            step="0.01"
            data-testid="nakit-satis-input"
            value={formData.nakitSatis}
            onChange={(e) => setFormData({ ...formData, nakitSatis: e.target.value })}
            className="border-0 border-b-2 border-border bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-primary font-mono text-lg transition-colors"
            placeholder="0.00"
          />
        </div>

        {/* Veresiye Satış */}
        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Veresiye Satış (₺)
          </label>
          <Input
            type="number"
            step="0.01"
            data-testid="veresiye-satis-input"
            value={formData.veresiyeSatis}
            onChange={(e) => setFormData({ ...formData, veresiyeSatis: e.target.value })}
            className="border-0 border-b-2 border-border bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-primary font-mono text-lg transition-colors"
            placeholder="0.00"
          />
        </div>

        {/* Nakit Gider */}
        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Nakit Gider Toplam (₺)
          </label>
          <Input
            type="number"
            step="0.01"
            data-testid="nakit-gider-input"
            value={formData.nakitGiderToplam}
            onChange={(e) => setFormData({ ...formData, nakitGiderToplam: e.target.value })}
            className="border-0 border-b-2 border-border bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-primary font-mono text-lg transition-colors"
            placeholder="0.00"
          />
        </div>

        {/* Mal Alım Tutarı */}
        <div className="bg-amber-50 border border-amber-200 p-4 -mx-2">
          <label className="block text-sm font-medium text-amber-800 mb-2">
            Bugün Alınan Mal Tutarı (₺)
          </label>
          <Input
            type="number"
            step="0.01"
            data-testid="mal-alim-input"
            value={formData.malAlimTutari}
            onChange={(e) => setFormData({ ...formData, malAlimTutari: e.target.value })}
            className="border-0 border-b-2 border-amber-300 bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-amber-500 font-mono text-lg transition-colors"
            placeholder="0.00"
          />
          <p className="text-xs text-amber-600 mt-2">Bu tutar giderlere dahil edilecektir</p>
        </div>

        {/* Kart POS Detayları */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-sm font-medium text-textPrimary">Kart POS Detayları</label>
            <Button
              type="button"
              onClick={handleAddKartDetay}
              data-testid="add-kart-btn"
              size="sm"
              className="bg-primary hover:bg-primaryHover text-primaryForeground"
            >
              <Plus className="h-4 w-4 mr-1" />
              Satır Ekle
            </Button>
          </div>
          <div className="space-y-3">
            {formData.kartDetaylari.map((kart, index) => (
              <div key={index} className="flex gap-2" data-testid={`kart-row-${index}`}>
                <Select
                  value={kart.bankaHesabiId}
                  onValueChange={(value) => handleKartDetayChange(index, 'bankaHesabiId', value)}
                >
                  <SelectTrigger data-testid={`kart-banka-select-${index}`} className="flex-1">
                    <SelectValue placeholder="Banka seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankalar.kartPos.map((banka) => (
                      <SelectItem key={banka.id} value={banka.id}>{banka.ad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  step="0.01"
                  value={kart.tutar}
                  onChange={(e) => handleKartDetayChange(index, 'tutar', e.target.value)}
                  className="w-32 font-mono"
                  placeholder="0.00"
                />
                <Button
                  type="button"
                  onClick={() => handleRemoveKartDetay(index)}
                  variant="outline"
                  size="icon"
                  className="text-error"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Havale Detayları */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-sm font-medium text-textPrimary">Havale Detayları</label>
            <Button
              type="button"
              onClick={handleAddHavaleDetay}
              data-testid="add-havale-btn"
              size="sm"
              className="bg-primary hover:bg-primaryHover text-primaryForeground"
            >
              <Plus className="h-4 w-4 mr-1" />
              Satır Ekle
            </Button>
          </div>
          <div className="space-y-3">
            {formData.havaleDetaylari.map((havale, index) => (
              <div key={index} className="flex gap-2" data-testid={`havale-row-${index}`}>
                <Select
                  value={havale.bankaHesabiId}
                  onValueChange={(value) => handleHavaleDetayChange(index, 'bankaHesabiId', value)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Banka seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankalar.havale.map((banka) => (
                      <SelectItem key={banka.id} value={banka.id}>{banka.ad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  step="0.01"
                  value={havale.tutar}
                  onChange={(e) => handleHavaleDetayChange(index, 'tutar', e.target.value)}
                  className="w-32 font-mono"
                  placeholder="0.00"
                />
                <Button
                  type="button"
                  onClick={() => handleRemoveHavaleDetay(index)}
                  variant="outline"
                  size="icon"
                  className="text-error"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Not */}
        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Not (Opsiyonel)
          </label>
          <Textarea
            data-testid="not-input"
            value={formData.not_}
            onChange={(e) => setFormData({ ...formData, not_: e.target.value })}
            className="min-h-[80px]"
            placeholder="Ek notlar..."
          />
        </div>

        {/* Totals Summary */}
        <div className="bg-surfaceSubtle p-4 space-y-2 font-mono text-sm">
          <div className="flex justify-between">
            <span className="text-textSecondary">Kart Toplam:</span>
            <span className="text-textPrimary font-bold">{formatCurrency(totals.kartToplam)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-textSecondary">Havale Toplam:</span>
            <span className="text-textPrimary font-bold">{formatCurrency(totals.havaleToplam)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-textSecondary">Toplam Satış:</span>
            <span className="text-textPrimary font-bold">{formatCurrency(totals.toplamSatis)}</span>
          </div>
          <div className="flex justify-between text-error">
            <span>Toplam Gider (Gider + Mal Alım):</span>
            <span className="font-bold">{formatCurrency(totals.toplamGider)}</span>
          </div>
          {totals.malAlim > 0 && (
            <div className="flex justify-between text-xs text-amber-600 pl-4">
              <span>- Mal Alım Tutarı:</span>
              <span>{formatCurrency(totals.malAlim)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-border">
            <span className="text-textPrimary font-bold">NET:</span>
            <span className="text-xl text-primary font-bold">{formatCurrency(totals.net)}</span>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          onClick={validateAndShowConfirm}
          data-testid="submit-btn"
          className="w-full h-12 bg-primary hover:bg-primaryHover text-primaryForeground font-medium tracking-wide uppercase transition-all active:translate-y-[1px]"
        >
          <Save className="h-5 w-5 mr-2" />
          Kaydet
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[450px]" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Günlük Kasa Onayı
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-textPrimary">
              Günlük kasayı bu şekilde onaylamayı kabul ediyor musunuz?
            </p>
            
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <p className="text-amber-800 font-medium mb-2">Kasanızda şu kadar nakit var mı?</p>
              <p className="text-3xl font-mono font-bold text-amber-900">
                {formatCurrency(totals.kasadakiNakit)}
              </p>
              <p className="text-xs text-amber-600 mt-2">
                (Nakit Satış - Giderler - Mal Alım)
              </p>
            </div>

            <div className="bg-surfaceSubtle p-3 space-y-1 font-mono text-sm">
              <div className="flex justify-between">
                <span className="text-textSecondary">Nakit Satış:</span>
                <span>{formatCurrency(parseFloat(formData.nakitSatis) || 0)}</span>
              </div>
              <div className="flex justify-between text-error">
                <span>Giderler:</span>
                <span>-{formatCurrency(parseFloat(formData.nakitGiderToplam) || 0)}</span>
              </div>
              <div className="flex justify-between text-error">
                <span>Mal Alım:</span>
                <span>-{formatCurrency(parseFloat(formData.malAlimTutari) || 0)}</span>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              data-testid="cancel-confirm-btn"
            >
              İptal
            </Button>
            <Button
              onClick={handleConfirmSubmit}
              data-testid="confirm-submit-btn"
              className="bg-primary hover:bg-primaryHover text-primaryForeground"
            >
              Evet, Onaylıyorum
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
